/**
 * BulkUploadTab
 *
 * Story 358: CSV upload tab for bulk corrections
 *
 * Features:
 * - Drag & drop file upload
 * - Preview with validation errors
 * - Confirmation before applying
 * - Upload history view
 *
 * Date: 2026-01-22
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Upload,
  FileText,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Download,
  History,
  FileDown,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { formatDistanceToNow } from 'date-fns';
import {
  uploadCsvForPreview,
  applyUploadCorrections,
  getUploadHistory,
  getUploadDownloadUrl,
  getTemplateDownloadUrl,
  UploadPreview,
  UploadHistoryItem,
} from '@/services/enrichmentReviewService';

export function BulkUploadTab() {
  const { selectedSite } = useSite();
  const { toast } = useToast();

  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [preview, setPreview] = useState<UploadPreview | null>(null);
  const [history, setHistory] = useState<UploadHistoryItem[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Load history on mount
  useEffect(() => {
    if (selectedSite?.id) {
      loadHistory();
    }
  }, [selectedSite?.id]);

  const loadHistory = async () => {
    if (!selectedSite?.id) return;
    setIsLoadingHistory(true);
    try {
      const data = await getUploadHistory(selectedSite.id);
      setHistory(data.history || []);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile?.name.endsWith('.csv')) {
      setFile(droppedFile);
      handleUpload(droppedFile);
    } else {
      toast({
        title: 'Invalid file',
        description: 'Please upload a CSV file',
        variant: 'destructive',
      });
    }
  }, [selectedSite?.id]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      handleUpload(selectedFile);
    }
  };

  const handleUpload = async (uploadFile: File) => {
    if (!selectedSite?.id) return;

    setIsUploading(true);
    setPreview(null);

    try {
      const data = await uploadCsvForPreview(selectedSite.id, uploadFile);
      setPreview(data);
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
      setFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleApply = async () => {
    if (!preview?.previewId) return;

    setIsApplying(true);
    try {
      const result = await applyUploadCorrections(preview.previewId);
      toast({
        title: 'Corrections applied',
        description: `Successfully updated ${result.appliedCount} product attributes.`,
      });
      setFile(null);
      setPreview(null);
      loadHistory();
    } catch (err) {
      toast({
        title: 'Apply failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsApplying(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      {!preview && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Upload Corrections CSV
                </CardTitle>
                <CardDescription className="mt-1">
                  Upload a CSV file to bulk-update product attributes
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" asChild>
                <a href={getTemplateDownloadUrl()} download>
                  <FileDown className="h-4 w-4 mr-2" />
                  Download Template
                </a>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isUploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p>Processing file...</p>
                </div>
              ) : (
                <>
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-2">
                    Drag and drop your CSV file here, or
                  </p>
                  <label>
                    <Button variant="outline" className="cursor-pointer" asChild>
                      <span>Browse Files</span>
                    </Button>
                    <input
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                  </label>
                  <p className="text-xs text-muted-foreground mt-4">
                    Required columns: product_id, field_name, corrected_value
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Optional columns: source_url, notes
                  </p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview */}
      {preview && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {file?.name}
              </CardTitle>
              <Button variant="outline" size="sm" onClick={handleReset}>
                Upload Different File
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-muted rounded-lg text-center">
                <p className="text-2xl font-bold">{preview.totalRows}</p>
                <p className="text-sm text-muted-foreground">Total Rows</p>
              </div>
              <div className="p-4 bg-green-500/10 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-600">{preview.validCount}</p>
                <p className="text-sm text-muted-foreground">Valid Corrections</p>
              </div>
              <div className="p-4 bg-red-500/10 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-600">{preview.errorCount}</p>
                <p className="text-sm text-muted-foreground">Errors</p>
              </div>
            </div>

            {/* Errors */}
            {preview.errors.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  Errors ({preview.errorCount})
                </h4>
                <ScrollArea className="h-40 border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Row</TableHead>
                        <TableHead>Field</TableHead>
                        <TableHead>Error</TableHead>
                        <TableHead>Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.errors.map((error, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono">{error.row}</TableCell>
                          <TableCell>{error.field}</TableCell>
                          <TableCell className="text-red-600">{error.error}</TableCell>
                          <TableCell className="font-mono text-xs max-w-[150px] truncate">
                            {error.value || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>
            )}

            {/* Preview Changes */}
            {preview.preview.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Preview of Changes (first 10)
                </h4>
                <ScrollArea className="h-48 border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>Field</TableHead>
                        <TableHead>Current</TableHead>
                        <TableHead>New</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.preview.map((change, i) => (
                        <TableRow key={i}>
                          <TableCell className="max-w-[200px] truncate">
                            {change.productName}
                          </TableCell>
                          <TableCell>{change.fieldName}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {change.currentValue || '-'}
                          </TableCell>
                          <TableCell className="font-medium text-green-600">
                            {change.newValue}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>
            )}

            {/* Actions */}
            {preview.validCount > 0 && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  This will update <strong>{preview.validCount}</strong> product attributes.
                  Changes will be marked as bulk corrections and tracked in history.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleReset}>
                Cancel
              </Button>
              <Button
                onClick={handleApply}
                disabled={isApplying || preview.validCount === 0}
              >
                {isApplying ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Apply {preview.validCount} Changes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Upload History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingHistory ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No upload history yet
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead className="text-right">Applied</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                  <TableHead>Uploaded By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium truncate max-w-[200px]">
                          {item.original_filename}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(item.file_size_bytes)}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className="bg-green-500/10 text-green-700">
                        {item.applied_count}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {item.error_count > 0 ? (
                        <Badge variant="destructive">{item.error_count}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {item.uploaded_by_name || item.uploaded_by_email}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(item.uploaded_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" asChild>
                        <a href={getUploadDownloadUrl(item.id)} title="Download original file">
                          <Download className="h-4 w-4" />
                        </a>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
